import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  alpha,
  IconButton,
  Paper,
  Chip,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  SwapHoriz as SwapHorizIcon,
  Queue as QueueIcon,
  Person as PersonIcon,
} from '@mui/icons-material';

const ConfirmarAlteracaoServicoModal = ({ 
  open, 
  onClose, 
  onSimAlterarServico,
  onNaoAlterarServico,
  senha,
  acaoEscolhida, // 'fila' ou 'atendente'
  atendenteEscolhido, // para quando for enviar para atendente
  customTheme,
  TransitionComponent,
  Backdrop 
}) => {

  const getAcaoTexto = () => {
    if (acaoEscolhida === 'fila') {
      return {
        titulo: 'Retornar para a Fila',
        descricao: 'A senha será devolvida ao início da fila de espera',
        icone: <QueueIcon sx={{ color: '#1976d2', fontSize: 28 }} />,
        cor: '#1976d2'
      };
    } else if (acaoEscolhida === 'atendente' && atendenteEscolhido) {
      return {
        titulo: `Enviar para ${atendenteEscolhido.nome}`,
        descricao: `A senha será enviada para o atendente ${atendenteEscolhido.nome}`,
        icone: <PersonIcon sx={{ color: '#9c27b0', fontSize: 28 }} />,
        cor: '#9c27b0'
      };
    }
    
          return {
        titulo: 'Ação Indefinida',
        descricao: 'Ação não definida',
        icone: <HelpOutlineIcon sx={{ color: '#757575', fontSize: 28 }} />,
        cor: '#757575'
      };
  };

  const acaoInfo = getAcaoTexto();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={TransitionComponent}
      TransitionProps={{
        timeout: {
          enter: 400,
          exit: 300
        }
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
        sx: {
          backgroundColor: alpha('#000', 0.5),
          backdropFilter: 'blur(2px)',
          transition: 'backdrop-filter 300ms cubic-bezier(0.4, 0, 0.2, 1)'
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha('#000', 0.2)}`,
          maxWidth: 500,
          mx: 2,
          mb: { xs: 1, sm: 4 },
          overflowY: 'auto',
          transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1) !important',
          '&.MuiDialog-paper': {
            transform: open ? 'translateY(0) !important' : 'translateY(100%) !important'
          }
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${alpha('#1976d2', 0.1)}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <HelpOutlineIcon sx={{ color: '#ed6c02', mr: 1.5 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Alterar Serviço?
          </Typography>
        </Box>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="fechar"
          sx={{ 
            color: '#757575',
            '&:hover': {
              color: '#424242',
              bgcolor: alpha('#757575', 0.1)
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {/* Informações da senha */}
        {senha && (
          <Paper elevation={0} sx={{ 
            p: 2, 
            mb: 3, 
            bgcolor: alpha('#f5f5f5', 0.5), 
            borderRadius: 2,
            border: '1px solid',
            borderColor: alpha('#1976d2', 0.2)
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Senha Selecionada
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Número</Typography>
                <Chip 
                  label={senha.numero || senha.senha} 
                  color="primary" 
                  sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                />
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">Serviço Atual</Typography>
                <Chip 
                  label={senha.servico_nome || senha.tipo} 
                  color="default" 
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
            </Box>
          </Paper>
        )}

        {/* Ação escolhida */}
        <Paper elevation={0} sx={{ 
          p: 2, 
          mb: 3, 
          bgcolor: alpha(acaoInfo.cor, 0.05), 
          borderRadius: 2,
          border: '1px solid',
          borderColor: alpha(acaoInfo.cor, 0.2)
        }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Ação Selecionada
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {acaoInfo.icone}
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 600, color: acaoInfo.cor }}>
                {acaoInfo.titulo}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {acaoInfo.descricao}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Pergunta principal */}
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <SwapHorizIcon sx={{ 
            fontSize: 48, 
            color: '#ed6c02',
            mb: 2 
          }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Deseja alterar o serviço da senha antes de prosseguir?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Você pode manter o serviço atual ou alterá-lo para outro serviço antes de executar a ação.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 2 }}>
        <Button 
          onClick={onNaoAlterarServico}
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            borderWidth: '2px',
            flex: 1,
            '&:hover': {
              borderWidth: '2px'
            }
          }}
        >
          Não, manter serviço atual
        </Button>
        <Button 
          onClick={onSimAlterarServico} 
          variant="contained" 
          color="warning"
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1,
            flex: 1,
            boxShadow: `0 4px 12px ${alpha('#ed6c02', 0.3)}`,
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha('#ed6c02', 0.4)}`,
            }
          }}
        >
          Sim, alterar serviço
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmarAlteracaoServicoModal; 